import React, { useState, useEffect } from 'react';
import { Package, Truck, CheckCircle, XCircle, Clock, Search, Download, Eye } from 'lucide-react';
import api from '../../utils/api';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders/admin/orders');
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.user?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/orders/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
      alert('Order status updated successfully!');
    } catch (error) {
      alert('Error updating order: ' + error.response?.data?.message);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-green-500';
      case 'shipped': return 'bg-blue-500';
      case 'processing': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'delivered': return <CheckCircle size={20} />;
      case 'shipped': return <Truck size={20} />;
      case 'processing': return <Package size={20} />;
      case 'cancelled': return <XCircle size={20} />;
      default: return <Clock size={20} />;
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-5xl font-black tracking-tighter mb-2">ORDERS</h1>
        <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
          ORDER_MANAGEMENT_SYSTEM
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_rgba(0,0,0,1)] mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Search Orders</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order number or customer name..."
                className="w-full pl-10 pr-4 py-3 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border-2 border-black font-bold text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-20">
          <div className="text-2xl font-black animate-pulse">LOADING_ORDERS...</div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white border-4 border-black p-12 text-center">
          <div className="text-gray-400 font-black text-2xl">NO ORDERS FOUND</div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order._id}
              className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  {/* Order Info */}
                  <div className="flex items-center gap-4">
                    <div className={`p-3 ${getStatusColor(order.status)} text-white border-2 border-black`}>
                      {getStatusIcon(order.status)}
                    </div>
                    <div>
                      <div className="font-black text-xl">{order.orderNumber}</div>
                      <div className="text-sm font-mono text-gray-600">
                        {order.user?.name} • {order.user?.email}
                      </div>
                      <div className="text-xs font-bold text-gray-500 mt-1">
                        {new Date(order.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Amount & Status */}
                  <div className="text-right">
                    <div className="font-black text-3xl mb-2">${order.pricing.total}</div>
                    <div className={`inline-block px-3 py-1 text-xs font-black uppercase border-2 border-black ${
                      order.status === 'delivered' ? 'bg-green-400' :
                      order.status === 'shipped' ? 'bg-blue-400' :
                      order.status === 'processing' ? 'bg-yellow-400' :
                      order.status === 'cancelled' ? 'bg-red-400' :
                      'bg-gray-400'
                    }`}>
                      {order.status}
                    </div>
                  </div>
                </div>

                {/* Items Summary */}
                <div className="bg-gray-50 border-2 border-black p-4 mb-4">
                  <div className="font-bold text-sm mb-2">ITEMS ({order.items.length}):</div>
                  <div className="space-y-1">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="text-sm font-mono flex justify-between">
                        <span>{item.name} × {item.quantity}</span>
                        <span className="font-bold">${item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-black text-white font-bold border-2 border-black hover:bg-gray-800"
                  >
                    <Eye size={16} />
                    VIEW DETAILS
                  </button>
                  {order.status === 'pending' && (
                    <button
                      onClick={() => updateOrderStatus(order._id, 'processing')}
                      className="flex-1 px-4 py-2 bg-yellow-400 text-black font-bold border-2 border-black hover:bg-yellow-300"
                    >
                      START PROCESSING
                    </button>
                  )}
                  {order.status === 'processing' && (
                    <button
                      onClick={() => updateOrderStatus(order._id, 'shipped')}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white font-bold border-2 border-black hover:bg-blue-600"
                    >
                      MARK AS SHIPPED
                    </button>
                  )}
                  {order.status === 'shipped' && (
                    <button
                      onClick={() => updateOrderStatus(order._id, 'delivered')}
                      className="flex-1 px-4 py-2 bg-green-500 text-white font-bold border-2 border-black hover:bg-green-600"
                    >
                      MARK AS DELIVERED
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={fetchOrders}
        />
      )}
    </div>
  );
};

// Order Detail Modal
const OrderDetailModal = ({ order, onClose, onUpdate }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border-4 border-yellow-400 shadow-[16px_16px_0px_rgba(255,255,0,1)] w-full max-w-4xl my-8">
        <div className="bg-yellow-400 p-4 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-2xl font-black">ORDER DETAILS: {order.orderNumber}</h2>
          <button onClick={onClose} className="text-2xl font-black hover:text-red-600">×</button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
          {/* Customer Info */}
          <div className="border-4 border-black p-4">
            <h3 className="font-black text-lg mb-3">CUSTOMER INFORMATION</h3>
            <div className="space-y-2 font-mono text-sm">
              <div><span className="font-bold">Name:</span> {order.shippingAddress.name}</div>
              <div><span className="font-bold">Email:</span> {order.user?.email}</div>
              <div><span className="font-bold">Phone:</span> {order.shippingAddress.phone}</div>
              <div><span className="font-bold">Address:</span> {order.shippingAddress.street}, {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</div>
            </div>
          </div>

          {/* Order Items */}
          <div className="border-4 border-black p-4">
            <h3 className="font-black text-lg mb-3">ORDER ITEMS</h3>
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center border-b-2 border-gray-200 pb-3">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-200 border-2 border-black flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package size={24} />
                      )}
                    </div>
                    <div>
                      <div className="font-black">{item.name}</div>
                      <div className="text-sm text-gray-600">Size: {item.size} • Qty: {item.quantity}</div>
                    </div>
                  </div>
                  <div className="font-black text-lg">${item.price * item.quantity}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="border-4 border-black p-4 bg-gray-50">
            <div className="space-y-2 font-bold">
              <div className="flex justify-between"><span>Subtotal:</span><span>${order.pricing.subtotal}</span></div>
              <div className="flex justify-between"><span>Shipping:</span><span>${order.pricing.shipping}</span></div>
              <div className="flex justify-between"><span>Tax:</span><span>${order.pricing.tax}</span></div>
              <div className="flex justify-between border-t-2 border-black pt-2 text-xl">
                <span>TOTAL:</span><span>${order.pricing.total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;
